<div class="mb-3">
  <div>
    <h1 class="pb-2 border-bottom fw-light" i18n>Erkennungsbegriffe</h1>

    <kendo-tabstrip class="mt-2">
      <kendo-tabstrip-tab i18n-title title="Begriffe" [selected]="true">
        <ng-template kendoTabContent>

    <p class="text-muted mt-2" i18n>
      Begriffe, mit denen die automatische Kategorie- und MwSt.-Ermittlung Shop-Texte zuordnet.
      Die Sprache bestimmt, wann ein Begriff greift: sprachspezifische Begriffe nur bei
      passender Shop-Sprache, neutrale immer. Änderungen wirken sofort beim nächsten Scan.
      Ein Begriff darf mehrere Artikelkategorien bedeuten — je Ziel eine eigene Zeile („Homewares"
      → Haushalt und → Heimtextilien); ein MwSt.-Begriff hat dagegen immer genau einen Satz.
    </p>

    <!-- #623/#579 decision 4: sign-off is the machine-checkable precondition for going live —
         the backend refuses Live VAT resolution while a single VAT row lacks it. -->
    <p class="text-muted" i18n>
      MwSt.-Begriffe brauchen zusätzlich eine steuerliche Freigabe: Erst wenn jede MwSt.-Zeile
      geprüft ist, lässt sich die MwSt.-Ermittlung produktiv schalten. Jede Änderung an einem
      Eintrag hebt seine Freigabe wieder auf — ein geänderter Eintrag ist ein ungeprüfter
      Eintrag. Artikelkategorien brauchen keine Freigabe.
    </p>

    @if (entryForm) {
      <form [formGroup]="entryForm" class="row g-2 align-items-end border rounded p-3 mb-3 bg-light">
        <div class="col-sm-2">
          <label class="form-label col-form-label-sm mb-0" i18n>Bereich</label>
          <kendo-dropdownlist
            [data]="subjectItems"
            formControlName="subject"
            textField="label"
            valueField="value"
            [valuePrimitive]="true"
            (valueChange)="onSubjectChange()"
          ></kendo-dropdownlist>
        </div>
        <div class="col-sm-3">
          <label class="form-label col-form-label-sm mb-0" i18n>Begriff</label>
          <input
            type="text"
            formControlName="term"
            class="form-control form-control-sm"
            i18n-placeholder
            placeholder="z. B. Marmelade"
            [ngClass]="{ 'is-invalid': f['term'].invalid && (f['term'].dirty || f['term'].touched) }"
          />
        </div>
        <div class="col-sm-2">
          <label class="form-label col-form-label-sm mb-0" i18n>Sprache</label>
          <kendo-dropdownlist
            [data]="languageItems"
            formControlName="language"
            textField="label"
            valueField="value"
            [valuePrimitive]="true"
            [defaultItem]="{ value: null, label: '—' }"
          ></kendo-dropdownlist>
        </div>
        <div class="col-sm-3">
          <label class="form-label col-form-label-sm mb-0" i18n>Ziel</label>
          <kendo-dropdownlist
            [data]="targetItems"
            formControlName="target"
            textField="label"
            valueField="value"
            [valuePrimitive]="true"
            [defaultItem]="{ value: null, label: '—' }"
          ></kendo-dropdownlist>
        </div>
        <!-- Auto width, not a fixed col-sm-2: in edit mode this holds two buttons (~180 px),
             which in a narrow window is wider than a twelfth-grid column — they then spilled
             out past the panel's right edge, „Abbrechen" half off-window. Auto-sized, the
             group keeps its natural width and wraps onto its own line when space runs out. -->
        <div class="col-12 col-sm-auto d-flex gap-2">
          <button
            class="btn btn-primary btn-sm flex-shrink-0"
            type="button"
            [asyncClick]="submitForm.bind(this)"
            [disabled]="saveBtn.disabled || entryForm.invalid || entryForm.pristine"
            #saveBtn="asyncClick"
            >
            @if (saveBtn.busy) { <span class="spinner-border spinner-border-sm me-1"></span> }
            @if (editingId) {
              <ng-container i18n>Speichern</ng-container>
            } @else {
              <ng-container i18n>Hinzufügen</ng-container>
            }
          </button>
          @if (editingId) {
            <button class="btn btn-secondary btn-sm flex-shrink-0" type="button" (click)="onCancelEdit()" i18n>
              Abbrechen
            </button>
          }
        </div>
      </form>
    }

    <!-- Backend #629: the export half of the curation round-trip — the downloaded file is
         committed verbatim into the backend repo (Catalog.TestData/resolution-vocabulary.json);
         the next seeding boot of each environment then picks the curated terms up. -->
    <div class="d-flex justify-content-end align-items-center mt-3">
      <span class="text-muted small me-2" i18n>
        Exportiert alle Begriffe als Seed-Datei (resolution-vocabulary.json) für das Backend-Repository.
      </span>
      <button
        class="btn btn-outline-secondary btn-sm flex-shrink-0"
        type="button"
        [asyncClick]="onExport.bind(this)"
        [disabled]="exportBtn.disabled"
        #exportBtn="asyncClick"
        >
        @if (exportBtn.busy) {
          <span class="spinner-border spinner-border-sm me-1"></span>
        } @else {
          <fa-icon [icon]="faDownload" class="me-1"></fa-icon>
        }
        <ng-container i18n>Exportieren</ng-container>
      </button>
    </div>

    <kendo-grid
      class="mt-2"
      [data]="gridView"
      [group]="groups"
      [sortable]="true"
      [sort]="sort"
      (sortChange)="onSortChange($event)"
      [resizable]="true"
      >
      <ng-template kendoGridNoRecordsTemplate>
        <app-grid-no-records></app-grid-no-records>
      </ng-template>

      <kendo-grid-column field="subjectLabel" i18n-title title="Bereich" [hidden]="true">
        <ng-template kendoGridGroupHeaderTemplate let-value="value" let-group="group">
          <strong>{{ value }}</strong>
          <span class="ms-1 text-muted">({{ group.items.length }})</span>
        </ng-template>
      </kendo-grid-column>

      <!-- Widths stay modest so the whole row — action buttons included — fits a narrow admin
           window without a horizontal scrollbar; „Begriff" takes whatever is left over. -->
      <kendo-grid-column field="term" i18n-title title="Begriff"></kendo-grid-column>
      <kendo-grid-column field="languageLabel" i18n-title title="Sprache" [width]="100">
        <ng-template kendoGridCellTemplate let-dataItem>
          <span [title]="dataItem.languageTitle">{{ dataItem.languageLabel }}</span>
        </ng-template>
      </kendo-grid-column>
      <kendo-grid-column field="targetLabel" i18n-title title="Ziel" [width]="110"></kendo-grid-column>
      <!-- #629 follow-up: the badge stays the OWNERSHIP dimension (System / Manuell gepflegt);
           where a term was first observed is its tooltip — present on Seeded rows too, since a
           sourced entry that travelled in via the export file keeps its provenance. -->
      <kendo-grid-column field="originLabel" i18n-title title="Herkunft" [width]="130">
        <ng-template kendoGridCellTemplate let-dataItem>
          @if (dataItem.origin === 'Seeded') {
            <span class="badge bg-secondary" [title]="dataItem.sourceTitle">{{ dataItem.originLabel }}</span>
          } @else {
            <span class="badge bg-info text-dark" [title]="dataItem.sourceTitle">{{ dataItem.originLabel }}</span>
          }
        </ng-template>
      </kendo-grid-column>

      <!-- #623: sign-off state AND the action that changes it, in one cell — an unverified
           MwSt. row is a go-live blocker, so it shows the way out of that state instead of a
           dead badge. Keeps the icon column at two buttons, which is what fits a narrow window. -->
      <kendo-grid-column field="verifiedAtDate" i18n-title title="Geprüft" [width]="140">
        <ng-template kendoGridCellTemplate let-dataItem>
          @if (!dataItem.needsSignOff) {
            <span class="text-muted">—</span>
          } @else if (dataItem.verifiedAtDate) {
            <!-- The day is the column value; who signed off and when exactly is the tooltip. -->
            <span class="badge bg-success" [title]="dataItem.verifiedByTitle">
              {{ dataItem.verifiedAtDate | date: 'shortDate' }}
            </span>
          } @else {
            <button
              class="btn btn-sm btn-outline-warning"
              type="button"
              i18n-title
              title="Steuerlich freigeben"
              [asyncClick]="onVerify(dataItem)"
              [disabled]="verifyBtn.disabled"
              #verifyBtn="asyncClick"
              >
              @if (verifyBtn.busy) {
                <span class="spinner-border spinner-border-sm me-1"></span>
              } @else {
                <fa-icon [icon]="faCheck" class="me-1"></fa-icon>
              }
              <ng-container i18n>Freigeben</ng-container>
            </button>
          }
        </ng-template>
      </kendo-grid-column>

      <kendo-grid-command-column title="" [width]="120" [resizable]="false">
        <ng-template kendoGridCellTemplate let-dataItem>
          <div class="d-flex justify-content-end gap-2">
            <button
              kendoGridEditCommand
              type="button"
              i18n-title
              title="Bearbeiten"
              (click)="onEdit(dataItem)"
              >
              <i><fa-icon [icon]="faEdit"></fa-icon></i>
            </button>
            <button type="button" class="btn btn-sm btn-outline-danger"
              i18n-title
              title="Löschen"
              [asyncClick]="onDelete(dataItem)"
              [disabled]="deleteBtn.disabled"
              #deleteBtn="asyncClick"
              >
              <i>
                @if (deleteBtn.busy) {
                  <span class="spinner-border spinner-border-sm"></span>
                } @else {
                  <fa-icon [icon]="faTrashAlt"></fa-icon>
                }
              </i>
            </button>
          </div>
        </ng-template>
      </kendo-grid-command-column>
    </kendo-grid>

        </ng-template>
      </kendo-tabstrip-tab>

      <!-- #623: the first half of the curation loop — the misses this vocabulary answers.
           A chip click lands the term in the form above, pre-selected subject + language. -->
      <kendo-tabstrip-tab i18n-title title="Beobachtungen">
        <ng-template kendoTabContent>
          <app-resolution-observations
            [categories]="categories"
            [vats]="vats"
            (prefill)="onPrefillFromObservation($event)"
          ></app-resolution-observations>
        </ng-template>
      </kendo-tabstrip-tab>

      <!-- #579 slice 3: the second sign-off guard for Live VAT — the signed AI evaluation. -->
      <kendo-tabstrip-tab i18n-title title="KI-Freigabe">
        <ng-template kendoTabContent>
          <app-vat-ai-acceptance></app-vat-ai-acceptance>
        </ng-template>
      </kendo-tabstrip-tab>
    </kendo-tabstrip>
  </div>
</div>
